import { css } from "@microsoft/fast-element";
import { borderRadiusCircular, borderRadiusLarge, borderRadiusMedium, borderRadiusNone, borderRadiusSmall, colorNeutralBackground4, colorNeutralBackground4Hover, colorNeutralBackgroundDisabled, colorNeutralBackgroundInverted, colorNeutralForeground1Hover, colorNeutralForeground1Pressed, colorNeutralForeground2, colorNeutralForeground2BrandHover, colorBrandBackground, colorBrandBackgroundHover, colorBrandBackgroundPressed, colorNeutralForeground2BrandPressed, colorNeutralForegroundDisabled, colorNeutralStroke2, colorNeutralStrokeDisabled, colorStrokeFocus2, colorSubtleBackground, colorTransparentBackground, colorTransparentStroke, curveEasyEase, display, durationFaster, fontFamilyBase, fontSizeBase200, fontSizeBase300, fontSizeBase400, fontSizeBase600, fontWeightRegular, fontWeightSemibold, lineHeightBase200, lineHeightBase300, lineHeightBase400, shadow2, shadow4, spacingHorizontalL, spacingHorizontalM, spacingHorizontalMNudge, spacingHorizontalS, spacingHorizontalSNudge, spacingHorizontalXS, spacingHorizontalXXXL, spacingVerticalS, spacingVerticalXXXL, strokeWidthThick, colorNeutralBackground1Hover, colorNeutralBackground1Pressed, colorNeutralForeground1, colorNeutralForeground2BrandSelected, colorNeutralForeground2Hover, colorNeutralForeground2Pressed, colorNeutralForeground2Selected, colorNeutralForegroundInverted, colorNeutralForegroundOnBrand, colorNeutralStroke1, colorNeutralStroke1Hover, colorNeutralStroke1Pressed, colorSubtleBackgroundHover, colorSubtleBackgroundPressed, colorSubtleBackgroundSelected, colorTransparentBackgroundHover, colorNeutralStrokeOnBrand, colorTransparentBackgroundPressed, colorTransparentBackgroundSelected, strokeWidthThin } from "@fluentui/web-components";
import { sizeHorizontalXL, sizeHorizontalXXL, sizeVerticalXL, sizeVerticalXXL, sizeVerticalXXXL } from "@fabric-msft/theme";
import { forcedColorsStylesheetBehavior } from "../../util/index.js";
import { circularState, iconOnlyState, largeState, mediumState, outlineState, pressedState, primaryState, smallState, squareState, subtleState, transparentState } from "../styles/states/states.js";
/**
 * The styles for the FilterPill component.
 *
 * @public
 * @privateRemarks
 */
export const styles = css `
  ${display("inline-flex")}

  :host {
    --icon-spacing: ${spacingHorizontalSNudge};
    position: relative;
    contain: layout style;
    vertical-align: middle;
    align-items: center;
    box-sizing: border-box;
    justify-content: center;
    text-align: center;
    text-decoration-line: none;
    margin: 0;
    /* 32px height tokenized */
    min-height: ${sizeVerticalXXXL};
    outline-style: none;
    background-color: ${colorNeutralBackground4};
    color: ${colorNeutralForeground1};
    border: ${strokeWidthThin} solid ${colorNeutralStroke1};
    padding: 0 ${spacingHorizontalM};
    /* 96px width = 3 * 32px */
    min-width: calc(${spacingHorizontalXXXL} * 3);
    border-radius: ${borderRadiusMedium};
    font-size: ${fontSizeBase300};
    /* @design-token-ok: font family already uses semantic token */
    font-family: ${fontFamilyBase};
    font-weight: ${fontWeightSemibold};
    line-height: ${lineHeightBase300};
    transition-duration: ${durationFaster};
    transition-property: background, border, color;
    transition-timing-function: ${curveEasyEase};
    cursor: pointer;
    user-select: none;
    width: fit-content;
  }

  .content {
    display: inherit;
    padding: 0 ${spacingHorizontalSNudge};
  }

  :host(${mediumState}) .content {
    padding: 0 ${spacingHorizontalMNudge};
  }

  :host(${largeState}) .content {
    padding: 0 ${spacingHorizontalM};
  }

  :host(:hover) {
    background-color: ${colorNeutralBackground1Hover};
    color: ${colorNeutralForeground1Hover};
    border-color: ${colorNeutralStroke1Hover};
  }

  :host(:hover:active) {
    background-color: ${colorNeutralBackground1Pressed};
    border-color: ${colorNeutralStroke1Pressed};
    color: ${colorNeutralForeground1Pressed};
    outline-style: none;
  }

  :host(:focus-visible) {
    border-color: ${colorTransparentStroke};
    outline: ${strokeWidthThick} solid ${colorTransparentStroke};
    box-shadow:
      ${shadow4},
      0 0 0 ${strokeWidthThick} ${colorStrokeFocus2};
  }

  @media screen and (prefers-reduced-motion: reduce) {
    transition-duration: 0.01ms;
  }

  ::slotted(svg) {
    /* Icon glyph dimensions mapped to design tokens (20px → base600 / XL spacing) */
    font-size: ${fontSizeBase600};
    height: ${sizeVerticalXL};
    width: ${sizeHorizontalXL};
  }

  :is([slot="start"], ::slotted([slot="start"])) {
    margin-inline-end: var(--icon-spacing);
    display: flex;
    justify-content: center;
    align-items: center;
  }

  :is([slot="end"], ::slotted([slot="end"])) {
    margin-inline-start: var(--icon-spacing);
    display: flex;
    justify-content: center;
    align-items: center;
  }

  :host(${iconOnlyState}) {
    width: ${sizeVerticalXXXL};
    height: ${sizeVerticalXXXL};
    min-width: ${sizeVerticalXXXL};
    min-height: ${sizeVerticalXXXL};
    max-width: ${sizeVerticalXXXL};
    max-height: ${sizeVerticalXXXL};
  }

  :host(${smallState}) {
    --icon-spacing: ${spacingHorizontalXS};
    min-height: ${sizeVerticalXXL};
    min-width: calc(${spacingHorizontalXXXL} * 2);
    padding: 0 ${spacingHorizontalS};
    border-radius: ${borderRadiusSmall};
    font-size: ${fontSizeBase200};
    line-height: ${lineHeightBase200};
    font-weight: ${fontWeightRegular};
  }

  :host(${smallState}${iconOnlyState}) {
    width: ${sizeVerticalXXL};
    height: ${sizeVerticalXXL};
    min-width: ${sizeVerticalXXL};
    min-height: ${sizeVerticalXXL};
    max-width: ${sizeVerticalXXL};
    max-height: ${sizeVerticalXXL};
  }

  :host(${largeState}) {
    min-height: calc(${spacingVerticalXXXL} + ${spacingVerticalS});
    border-radius: ${borderRadiusLarge};
    padding: 0 ${spacingHorizontalL};
    font-size: ${fontSizeBase400};
    line-height: ${lineHeightBase400};
  }

  :host(${largeState}${iconOnlyState}) {
    min-width: calc(${spacingVerticalXXXL} + ${spacingVerticalS});
    max-width: calc(${spacingVerticalXXXL} + ${spacingVerticalS});
    max-height: calc(${spacingVerticalXXXL} + ${spacingVerticalS});
    min-height: calc(${spacingVerticalXXXL} + ${spacingVerticalS});
    width: calc(${spacingVerticalXXXL} + ${spacingVerticalS});
    height: calc(${spacingVerticalXXXL} + ${spacingVerticalS});
  }

  :host(${largeState}) ::slotted(svg) {
    /* Large icon size mapped to tokens (24px → base600 font & XXL spacing) */
    font-size: ${fontSizeBase600};
    height: ${sizeVerticalXXL};
    width: ${sizeHorizontalXXL};
  }

  :host(:is(${circularState}, ${circularState}:focus-visible)) {
    border-radius: ${borderRadiusCircular};
  }

  :host(:is(${squareState}, ${squareState}:focus-visible)) {
    border-radius: ${borderRadiusNone};
  }

  :host(${primaryState}) {
    background-color: ${colorNeutralBackground4};
    color: ${colorNeutralForeground1};
    border-color: transparent;
  }

  :host(${primaryState}:hover) {
    background-color: ${colorNeutralBackground4Hover};
    color: ${colorNeutralForeground1};
  }

  :host(${primaryState}:is(:hover, :hover:active)) {
    border-color: ${colorNeutralStroke2};
  }

  :host(${primaryState}:hover:active) {
    border-color: ${colorTransparentStroke};
    background-color: ${colorNeutralBackgroundInverted};
    color: ${colorNeutralForegroundOnBrand};
  }

  :host(${primaryState}:focus-visible) {
    border-color: ${colorNeutralForegroundOnBrand};
    box-shadow:
      ${shadow2},
      0 0 0 ${strokeWidthThick} ${colorStrokeFocus2};
  }

  :host(${outlineState}) {
    background-color: ${colorTransparentBackground};
    color: ${colorNeutralForeground2};
  }

  :host(${outlineState}:hover) {
    background-color: ${colorTransparentBackgroundHover};
  }

  :host(${outlineState}:hover:active) {
    background-color: ${colorTransparentBackgroundPressed};
  }

  :host(${subtleState}) {
    background-color: ${colorSubtleBackground};
    color: ${colorNeutralForeground2};
    border-color: transparent;
  }

  :host(${subtleState}:hover) {
    background-color: ${colorSubtleBackgroundHover};
    color: ${colorNeutralForeground2Hover};
    border-color: transparent;
  }

  :host(${subtleState}:hover:active) {
    background-color: ${colorSubtleBackgroundPressed};
    color: ${colorNeutralForeground2Pressed};
    border-color: transparent;
  }

  :host(${subtleState}:hover) ::slotted(svg) {
    fill: ${colorNeutralForeground2BrandHover};
  }

  :host(${subtleState}:hover:active) ::slotted(svg) {
    fill: ${colorNeutralForeground2BrandPressed};
  }

  :host(${transparentState}) {
    background-color: ${colorTransparentBackground};
    color: ${colorNeutralForeground2};
  }

  :host(${transparentState}:hover) {
    background-color: ${colorTransparentBackgroundHover};
    color: ${colorNeutralForeground2BrandHover};
  }

  :host(${transparentState}:hover:active) {
    background-color: ${colorTransparentBackgroundPressed};
    color: ${colorNeutralForeground2BrandPressed};
  }

  :host(:is(${transparentState}, ${transparentState}:is(:hover, :active))) {
    border-color: transparent;
  }

  :host([appearance="primary-on-brand"]) {
    background-color: ${colorBrandBackground};
    color: ${colorNeutralForegroundOnBrand};
    border-color: ${colorNeutralStrokeOnBrand};
  }

  :host(
    [appearance="primary-on-brand"]:is(:hover, :hover:active):not(
        :focus-visible
      )
  ) {
    border-color: ${colorNeutralStrokeOnBrand};
  }

  :host([appearance="primary-on-brand"]:is(:hover, :hover:active)) {
    color: ${colorNeutralForegroundOnBrand};
    background-color: ${colorBrandBackgroundHover};
  }

  :host([appearance="primary-on-brand"]:hover:active) {
    background-color: ${colorBrandBackgroundPressed};
  }

  :host([appearance="primary-on-brand"]:focus-visible) {
    border-color: ${colorNeutralForegroundOnBrand};
    box-shadow:
      ${shadow2},
      0 0 0 ${strokeWidthThick} ${colorStrokeFocus2};
  }
  :host(
    :is(
      :disabled,
      [disabled-focusable],
      [appearance]:disabled,
      [appearance][disabled-focusable]
    )
  ),
  :host(
    :is(
      :disabled,
      [disabled-focusable],
      [appearance]:disabled,
      [appearance][disabled-focusable]
    ):hover
  ),
  :host(
    :is(
        :disabled,
        [disabled-focusable],
        [appearance]:disabled,
        [appearance][disabled-focusable]
      ):hover:active
  ) {
    background-color: ${colorNeutralBackgroundDisabled};
    border-color: ${colorNeutralStrokeDisabled};
    color: ${colorNeutralForegroundDisabled};
    cursor: not-allowed;
  }

  :host(${primaryState}:is(:disabled, [disabled-focusable])),
  :host(
    ${primaryState}:is(:disabled, [disabled-focusable]):is(
      :hover,
      :hover:active
    )
  ) {
    border-color: transparent;
  }

  :host(${outlineState}:is(:disabled, [disabled-focusable])),
  :host(
    ${outlineState}:is(:disabled, [disabled-focusable]):is(
      :hover,
      :hover:active
    )
  ) {
    background-color: ${colorTransparentBackground};
  }

  :host(${subtleState}:is(:disabled, [disabled-focusable])),
  :host(
    ${subtleState}:is(:disabled, [disabled-focusable]):is(:hover, :hover:active)
  ) {
    background-color: ${colorTransparentBackground};
    border-color: transparent;
  }

  :host(${transparentState}:is(:disabled, [disabled-focusable])),
  :host(
    ${transparentState}:is(:disabled, [disabled-focusable]):is(
      :hover,
      :hover:active
    )
  ) {
    border-color: transparent;
    background-color: ${colorTransparentBackground};
  }

  :host(${pressedState}) {
    border-color: ${colorTransparentStroke};
    background-color: ${colorNeutralBackgroundInverted};
    color: ${colorNeutralForegroundInverted};
    border-width: ${strokeWidthThin};
  }

  :host(${pressedState}:hover) {
    border-color: ${colorNeutralStroke1Hover};
    background-color: ${colorNeutralBackground1Hover};
    color: ${colorNeutralForeground1Hover};
  }

  :host(${pressedState}:active) {
    border-color: ${colorNeutralStroke1Pressed};
    background-color: ${colorNeutralBackground1Pressed};
    color: ${colorNeutralForeground1Pressed};
  }

  :host(${pressedState}${primaryState}) {
    border-color: ${colorTransparentStroke};
    background-color: ${colorNeutralBackgroundInverted};
    color: ${colorNeutralForegroundInverted};
  }

  :host(${pressedState}${primaryState}:hover) {
    background-color: ${colorNeutralForeground1};
    color: ${colorNeutralForegroundInverted};
  }

  :host(${pressedState}${primaryState}:active) {
    border-color: ${colorTransparentStroke};
    background-color: ${colorNeutralBackgroundInverted};
    color: ${colorNeutralForegroundInverted};
  }

  :host(${pressedState}${subtleState}) {
    border-color: transparent;
    background-color: ${colorSubtleBackgroundSelected};
    color: ${colorNeutralForeground2Selected};
  }

  :host(${pressedState}${subtleState}:hover) {
    background-color: ${colorSubtleBackgroundHover};
    color: ${colorNeutralForeground2Hover};
  }

  :host(${pressedState}${subtleState}:active) {
    background-color: ${colorSubtleBackgroundPressed};
    color: ${colorNeutralForeground2Pressed};
  }

  :host(${pressedState}${outlineState}),
  :host(${pressedState}${transparentState}) {
    background-color: ${colorTransparentBackgroundSelected};
    color: ${colorNeutralForeground1};
    border-color: ${colorNeutralStroke1Pressed};
  }

  :host(${pressedState}${outlineState}) {
    border-width: ${strokeWidthThick};
  }

  :host(${pressedState}${outlineState}:hover),
  :host(${pressedState}${transparentState}:hover) {
    background-color: ${colorTransparentBackgroundHover};
  }

  :host(${pressedState}${outlineState}:active),
  :host(${pressedState}${transparentState}:active) {
    background-color: ${colorTransparentBackgroundPressed};
    border-width: ${strokeWidthThick};
  }

  :host(${pressedState}${transparentState}) {
    border-color: transparent;
    color: ${colorNeutralForeground2BrandSelected};
  }

  :host(${pressedState}${transparentState}:hover) {
    color: ${colorNeutralForeground2BrandHover};
  }

  :host(${pressedState}${transparentState}:active) {
    color: ${colorNeutralForeground2BrandPressed};
  }
`.withBehaviors(forcedColorsStylesheetBehavior(css `
    :host(${pressedState}),
    :host(${pressedState}${primaryState}),
    :host(${pressedState}${subtleState}),
    :host(${pressedState}${outlineState}),
    :host(${pressedState}${transparentState}) {
      background: SelectedItem;
      color: SelectedItemText;
    }
  `));
//# sourceMappingURL=filter-pill.styles.js.map